import React, { useCallback, useEffect, useState, useContext } from 'react';
import moment from 'moment';
import i18n from 'i18n-js';
import PropTypes from 'prop-types';
import BaseModal from 'components/UI/BaseModal';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import TablePagination from '@mui/material/TablePagination';
import Link from '@mui/material/Link';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { ReservationsActions } from 'state/modules/reservations';
import {
  changeBulkReservationsStatus,
  resetBulkReservationsStatus,
} from 'state/modules/reservations/actions';
import { useDispatch, useSelector } from 'react-redux';
import { buildUrl } from 'utils';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import ErrorIcon from '@mui/icons-material/Error';
import { UserContext } from 'context';

const ModalState = Object.freeze({
  CONFIRM_REASSIGN: Symbol('CONFIRM_REASSIGN'),
  CONFIRM_CANCEL: Symbol('CONFIRM_CANCEL'),
  SUCCESS: Symbol('SUCCESS'),
  FAILED: Symbol('FAILED'),
});

const ChangeVehicleStatusReservationsConflictModal = ({
  open,
  onClose,
  onCloseAll,
  vehicleId,
  startTime,
  endTime,
  newStatus,
  offlineReason,
  description,
}) => {
  const dispatch = useDispatch();
  const [currentState, setCurrentState] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const { localeFormat } = useSelector((state) => state.organizations);
  const { orgType } = useContext(UserContext);
  const isOwner = orgType === 'msOwnerOrg';
  const {
    reservations,
    loading,
    fetchingBulkReservationsAction,
    fetchedBulkReservationsActionSuccess,
    errors,
  } = useSelector((state) => state.reservations);

  const [params] = useState({
    vehicle_id: vehicleId,
    statuses: ['RESERVED'],
    sort_field: 'reserved_times',
    sort_desc: false,
    include_overlapping: true,
    start_reserved: startTime,
    end_reserved: endTime,
  });

  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
      </span>
    );
  };

  DatetimeField.propTypes = {
    datetime: PropTypes.string.isRequired,
    tz: PropTypes.string,
  };

  const getTimeZone = (reservation) => {
    if (reservation?.vehicle_group?.id && !isOwner) {
      return reservation.vehicle_group.time_zone;
    } else if (reservation?.vehicle_group?.parent?.id && isOwner) {
      return reservation.vehicle_group.parent.time_zone;
    }
  };

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const fetchReservations = useCallback(
    (params) => {
      // Remove end_reserved from params if null
      if (params.end_reserved === null) {
        delete params.end_reserved;
      }

      dispatch(
        ReservationsActions.fetchReservations({
          ...params,
        })
      );
    },
    [dispatch]
  );

  const handleAction = (action) => {
    const payload = {
      action,
      reservation_ids: reservations.map((r) => r.id),
      start_time: startTime,
      end_time: endTime || null,
      vehicle: {
        status: newStatus,
        offline_reason: offlineReason,
        description: description,
      },
    };
    dispatch(changeBulkReservationsStatus(payload));
  };

  const confirmAction = (action) => {
    if (action === 'CANCEL') {
      setCurrentState(ModalState.CONFIRM_CANCEL);
    } else if (action === 'REASSIGN') {
      setCurrentState(ModalState.CONFIRM_REASSIGN);
    }
  };

  const handleClose = () => {
    dispatch(resetBulkReservationsStatus());
    onCloseAll();
  };

  useEffect(() => {
    if (open) {
      fetchReservations(params);
    }
  }, [open, params, fetchReservations]);

  useEffect(() => {
    if (fetchedBulkReservationsActionSuccess) {
      setCurrentState(ModalState.SUCCESS);
    }
  }, [fetchedBulkReservationsActionSuccess]);

  useEffect(() => {
    if (errors && !fetchedBulkReservationsActionSuccess && !fetchingBulkReservationsAction) {
      setCurrentState(ModalState.FAILED);
    }
  }, [errors, fetchedBulkReservationsActionSuccess, fetchingBulkReservationsAction]);

  const isResultModal = currentState === ModalState.SUCCESS || currentState === ModalState.FAILED;

  return (
    <>
      {!isResultModal && (
        <BaseModal
          size="xl"
          open={open}
          handleClick={onClose}
          enableCloseButton={true}
          title={i18n.t('modals.conflicting_reservations.title')}
        >
          <Grid container>
            <Grid size={12}>
              <Typography variant="body1" gutterBottom>
                {i18n.t('modals.conflicting_reservations.description_1')}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {i18n.t('modals.conflicting_reservations.description_2')}
              </Typography>
            </Grid>
            <Grid size={12} sx={{ mt: 4, mb: 2 }}>
              {loading ? (
                <Box alignItems="center" display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Customer</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reservations
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>
                                <Link
                                  target="_blank"
                                  rel="noopener"
                                  href={buildUrl(`/reservations/${row.id}`)}
                                >
                                  {row.id}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {row.user.first_name} {row.user.last_name}
                              </TableCell>
                              <TableCell>{row.start_reserved_location.name}</TableCell>
                              <TableCell>
                                <DatetimeField
                                  datetime={row.start_reserved}
                                  tz={getTimeZone(row)}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={reservations.length || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={() => {}}
                    rowsPerPageOptions={[]}
                  />
                </>
              )}
            </Grid>
            <Grid container size={12} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Grid>
                <Button
                  disabled={fetchingBulkReservationsAction}
                  onClick={() => confirmAction('CANCEL')}
                >
                  {i18n.t('modals.conflicting_reservations.cancel_reservations')}
                </Button>
              </Grid>
              <Grid offset={1}>
                <Badge
                  overlap="rectangular"
                  badgeContent={
                    <Tooltip title={i18n.t('modals.conflicting_reservations.reassign_tooltip')}>
                      <InfoIcon color="primary" fontSize="small" />
                    </Tooltip>
                  }
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Button
                    disabled={fetchingBulkReservationsAction}
                    onClick={() => confirmAction('REASSIGN')}
                  >
                    {i18n.t('modals.conflicting_reservations.reassign_reservations')}
                  </Button>
                </Badge>
              </Grid>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.CONFIRM_CANCEL && (
        <BaseModal
          size="md"
          enableCloseButton={false}
          open={currentState === ModalState.CONFIRM_CANCEL}
          onClose={() => setCurrentState(null)}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            data-cy="confirm-cancel"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid item>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid item>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_action')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_cancel_description')}
              </Typography>
            </Grid>
            <Grid item container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setCurrentState(null)}
                sx={{ mr: 2 }}
              >
                {i18n.t('modals.conflicting_reservations.cancel')}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleAction('CANCEL')}>
                {i18n.t('modals.conflicting_reservations.confirm')}
              </Button>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.CONFIRM_REASSIGN && (
        <BaseModal
          size="md"
          enableCloseButton={false}
          open={currentState === ModalState.CONFIRM_REASSIGN}
          onClose={() => setCurrentState(null)}
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            data-cy="confirm-reassign"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_action')}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.confirm_reassign_desc_1')}{' '}
                <b>{i18n.t('modals.conflicting_reservations.confirm_reassign_desc_2')}</b>
              </Typography>
            </Grid>
            <Grid item container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setCurrentState(null)}
                sx={{ mr: 2 }}
              >
                {i18n.t('modals.conflicting_reservations.cancel')}
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleAction('REASSIGN')}>
                {i18n.t('modals.conflicting_reservations.confirm')}
              </Button>
            </Grid>
          </Grid>
        </BaseModal>
      )}
      {currentState === ModalState.SUCCESS && (
        <BaseModal size="md" open={currentState === ModalState.SUCCESS} handleClick={handleClose}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            data-cy="action-success"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid>
              <LibraryAddCheckIcon color="primary" />
            </Grid>
            <Grid>
              <Typography variant="h6" color="primary" align="center">
                {i18n.t('modals.conflicting_reservations.success_title')}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body1" align="center">
                {i18n.t('modals.conflicting_reservations.success_message')}
              </Typography>
            </Grid>
          </Grid>
        </BaseModal>
      )}

      {currentState === ModalState.FAILED && (
        <BaseModal size="md" open={currentState === ModalState.FAILED} handleClick={handleClose}>
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            data-cy="action-failed"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            <Grid>
              <ErrorIcon color="error" />
            </Grid>
            <Grid>
              <Typography variant="h6" color="error" align="center">
                {i18n.t('modals.conflicting_reservations.failed_title')}
              </Typography>
            </Grid>
            <Grid>
              <Typography variant="body1" align="center">
                {errors?.message || i18n.t('modals.conflicting_reservations.failed_message')}
              </Typography>
            </Grid>
          </Grid>
        </BaseModal>
      )}
    </>
  );
};

ChangeVehicleStatusReservationsConflictModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleId: PropTypes.number,
  startTime: PropTypes.string,
  endTime: PropTypes.string,
  newStatus: PropTypes.string,
  offlineReason: PropTypes.string,
  description: PropTypes.string,
  onCloseAll: PropTypes.func,
};

export default ChangeVehicleStatusReservationsConflictModal;
